import type { Course, CourseStep, Hotspot, StepKind } from '../core/course.js'
import type { RawCourse, RawImage, RawObject } from './types.js'

/**
 * Převod syrových dat ze safetutor.cz na doménový model.
 *
 * Tady a jen tady se tolerují historické zvláštnosti produkčních dat:
 *  - UTF-8 BOM na začátku souboru (modul4.json)
 *  - překlepnutý klíč `videoEndWrongtUrl` (57× v datech)
 *  - `correctAnswer` / `correctImage` jednou number, jednou string, jednou ""
 *  - překlepnutý klíč `hihglightImagerUrl` u hotspotů
 *  - desetinné a duplicitní hodnoty v `index`
 */

/** Odstraní UTF-8 BOM, který má modul4.json. `JSON.parse` na něm jinak spadne. */
export function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
}

export function parseCourseJson(text: string): RawCourse {
  return JSON.parse(stripBom(text)) as RawCourse
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return fallback
}

function toText(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function toKind(value: unknown): StepKind {
  return value === 'video' || value === 'question' || value === 'highlight' ? value : 'question'
}

/**
 * Klíče jsou "1".."4". JS objekty s celočíselnými klíči se iterují vzestupně,
 * takže původní aplikace dostávala stejné pořadí — replikujeme explicitně.
 */
function dictToSortedArray<T>(dict: Record<string, T> | undefined): T[] {
  if (!dict) return []
  return Object.keys(dict)
    .sort((a, b) => {
      const na = Number(a)
      const nb = Number(b)
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb
      return a.localeCompare(b)
    })
    .map((k) => dict[k] as T)
}

function normalizeHotspot(raw: RawImage): Hotspot {
  return {
    baseImageUrl: toText(raw.baseImageUrl),
    // Překlep je v produkčních datech; podporujeme i správný tvar pro budoucí data.
    highlightImageUrl: toText(
      raw.hihglightImagerUrl ?? (raw as { highlightImageUrl?: string }).highlightImageUrl,
    ),
    width: toNumber(raw.width),
    height: toNumber(raw.height),
    scale: toNumber(raw.scale, 1),
    posFromTopPercent: toNumber(raw.posFromTopinPercent),
    posFromLeftPercent: toNumber(raw.posFromLeftinPercent),
  }
}

function normalizeStep(raw: RawObject, position: number): CourseStep {
  return {
    position,
    legacyIndex: toNumber(raw.index),
    kind: toKind(raw.type),

    videoUrl: toText(raw.videoUrl),
    imageUrl: toText(raw.imageUrl),
    correctVideoUrl: toText(raw.videoEndCorrectUrl),
    // Překlepnutý klíč má přednost jen když správný chybí.
    wrongVideoUrl: toText(raw.videoEndWrongUrl ?? raw.videoEndWrongtUrl),

    question: toText(raw.question),
    answers: dictToSortedArray(raw.answers).map(toText),
    correctAnswer: toNumber(raw.correctAnswer),

    hotspots: dictToSortedArray(raw.images).map(normalizeHotspot),
    correctHotspot: toNumber(raw.correctImage),

    uiLayout: toText(raw.uiLayout),

    timeLimitSec: toNumber(raw.time),
    timeWarningSec: toNumber(raw.timeWarning),
    pointsMax: toNumber(raw.pointsMax),
    pointsSecondAttempt: toNumber(raw.pointsSecondAttempt),
    pointsTimePenalty: toNumber(raw.pointsTimePenalty),
  }
}

export function normalizeCourse(raw: RawCourse): Course {
  // Klíče "object1", "object2"… nejsou celočíselné, takže Object.values
  // zachovává pořadí vložení — stejně jako `for..in` v původní aplikaci.
  const steps = Object.values(raw.objects ?? {}).map(normalizeStep)

  return {
    controls: raw.controls === true,
    pointsToPassPercent: toNumber(raw.pointsToPassPercent),
    steps,
  }
}
